import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Papa from "papaparse";
import { storage, db, auth } from "./firebase";
import { ref, getDownloadURL } from "firebase/storage";
import {
  doc, getDoc, updateDoc, setDoc,
  collection, query, orderBy, limit, startAfter, getDocs
} from "firebase/firestore";
import { getPricingForCountrySync, formatCurrency, getPaymentAmount, initializeExchangeRates, BACKEND_URL } from "./constants";
import getSymbolFromCurrency from "currency-symbol-map";
import { useAuth } from "./AuthContext";
import { getSpecialtyPricingInfo } from "./constants";

// eslint-disable-next-line no-unused-vars
const InteractiveLoading = ({ specialty, currentStage = 'fetching', progress = 0 }) => {
  const [dots, setDots] = useState('');
  const [tipIndex, setTipIndex] = useState(0);

  // Define loadingTips before using it
  const loadingTips = [
    "💡 Programs are ranked by compatibility with your profile",
    "🎯 Use filters to narrow down your target programs",
    "📊 Step 2 CK scores help identify realistic targets",
    "⭐ Save favorites to track your top choices",
    "🌍 IMG-friendly programs are highlighted for international students",
    "📈 Familiarity scores show your match probability",
    "🔍 Click on any program to see detailed requirements",
    "📋 Create a balanced list of reach, target, and safety programs"
  ];

  // Animated dots for loading text
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Rotate through loading tips
  useEffect(() => {
    const interval = setInterval(() => {
      setTipIndex(prev => (prev + 1) % loadingTips.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [loadingTips.length]);

  const loadingStages = {
    fetching: {
      icon: '📡',
      title: 'Fetching Data',
      description: `Loading ${specialty} programs from database`,
      color: 'blue'
    },
    parsing: {
      icon: '⚙️',
      title: 'Processing Data',
      description: 'Parsing and organizing program information',
      color: 'purple'
    },
    calculating: {
      icon: '🧮',
      title: 'Calculating Scores',
      description: 'Computing familiarity scores for your profile',
      color: 'green'
    },
    finalizing: {
      icon: '✨',
      title: 'Almost Ready',
      description: 'Preparing your personalized program list',
      color: 'orange'
    }
  };

  const stage = loadingStages[currentStage] || loadingStages.fetching;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        {/* Main Loading Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-black/8 dark:border-gray-700">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="text-4xl mb-4">{stage.icon}</div>
            <h2 className="text-xl font-semibold text-black dark:text-white mb-2 tracking-[-0.02em]">
              {stage.title}{dots}
            </h2>
            <p className="text-[14px] text-gray-500 dark:text-gray-400">
              {stage.description}
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-[13px] text-gray-500 dark:text-gray-400 mb-2">
              <span>Progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-black/10 dark:bg-white/20 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-black dark:bg-white rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Stage Indicators */}
          <div className="grid grid-cols-4 gap-2 mb-8">
            {Object.entries(loadingStages).map(([key, stageInfo], index) => {
              const isActive = key === currentStage;
              const isCompleted = Object.keys(loadingStages).indexOf(currentStage) > index;
              return (
                <div
                  key={key}
                  className={`p-3 rounded-xl text-center transition-all duration-300 border ${
                    isActive
                      ? 'bg-black/[0.06] dark:bg-white/10 border-black/20 dark:border-gray-500'
                      : isCompleted
                      ? 'bg-black/[0.04] dark:bg-white/5 border-black/10 dark:border-gray-600'
                      : 'bg-black/[0.02] dark:bg-white/[0.03] border-black/6 dark:border-gray-700'
                  }`}
                >
                  <div className="text-lg mb-1">{isCompleted ? '✅' : stageInfo.icon}</div>
                  <div className={`text-xs font-medium ${isActive ? 'text-black dark:text-white' : isCompleted ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}`}>
                    {stageInfo.title}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Rotating Tips */}
          <div className="bg-black/[0.02] dark:bg-white/5 rounded-2xl p-4 border border-black/8 dark:border-gray-700">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-black/5 dark:bg-white/10 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-black dark:text-white mb-1">Did you know?</h4>
                <p className="text-[13px] text-gray-600 dark:text-gray-400 transition-opacity duration-500">
                  {loadingTips[tipIndex]}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-6">
          <div className="inline-flex items-center bg-black/[0.03] dark:bg-white/5 rounded-full px-4 py-2 border border-black/8 dark:border-gray-700">
            <div className="w-2 h-2 bg-gray-500 dark:bg-gray-400 rounded-full animate-ping mr-2" />
            <span className="text-[13px] text-gray-600 dark:text-gray-400 font-medium">
              Loading {specialty} programs{dots}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Heart icon component for favorites
const Heart = ({ size = 20, className = "", filled = false }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
  </svg>
);



// Filter options constants - defined outside component
// eslint-disable-next-line no-unused-vars
const FILTER_OPTIONS = {
  countries: [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia",
    "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
    "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei",
    "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada", "Cape Verde",
    "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo",
    "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica",
    "Dominican Republic", "East Timor", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea",
    "Eritrea", "Estonia", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia",
    "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
    "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland",
    "Israel", "Italy", "Ivory Coast", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya",
    "Kiribati", "North Korea", "South Korea", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon",
    "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Macedonia",
    "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands",
    "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia",
    "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands",
    "New Zealand", "Nicaragua", "Niger", "Nigeria", "Norway", "Oman", "Pakistan", "Palau",
    "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
    "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia",
    "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe",
    "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia",
    "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka",
    "Sudan", "Suriname", "Swaziland", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan",
    "Tanzania", "Thailand", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey",
    "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom",
    "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
    "Yemen", "Zambia", "Zimbabwe"
  ],
  states: [
    "AL", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", 
    "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", 
    "ND", "OH", "OK", "OR", "PA", "PR", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", 
    "WI", "DC"
  ],
  graduationTimes: [
    "<1 year",
    "1–2 years", 
    "3–5 years",
    ">5 years"
  ],
  step1Options: [
    "Pass in 1st attempt",
    "Pass in 2nd attempt", 
    "Pass in 3rd attempt",
    "Fail"
  ],
  step2Ranges: [
    "Not passed",
    "209-214", "215-219", "220-224", "225-229", "230-234", "235-239", "240-244", "245-249",
    "250-254", "255-259", "260-264", "265-269", "270-274", "275-279", "280-284", "285-289",
    "290-294", "295-299"
  ],
  usceOptions: [
    "None",
    "<1 month",
    "1–3 months", 
    "3+ months"
  ],
  visaOptions: [
    "No need",
    "J1",
    "H1", 
    "J1 or H1"
  ]
};

// Skeleton row — shown while the first real rows stream in
const SkeletonRow = ({ colCount }) => (
  <tr className="animate-pulse">
    {Array.from({ length: colCount || 6 }).map((_, i) => (
      <td key={i} className="px-6 py-4 border-x-0 border-t-0">
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" style={{ width: `${55 + (i * 17) % 35}%` }} />
      </td>
    ))}
  </tr>
);

function TablePage() {
  const { specialty } = useParams();
  const navigate = useNavigate();

  // Lazy initialisers: read localStorage synchronously on first render.
  // Returning visitors see the full table on the very first paint — zero async wait.
  const [tableData, setTableData] = useState(() => {
    try {
      const c = localStorage.getItem(`table_${specialty}_v2`);
      if (c) { const { data } = JSON.parse(c); return data || []; }
    } catch (_) {}
    return [];
  });
  const [headers, setHeaders] = useState(() => {
    try {
      const c = localStorage.getItem(`table_${specialty}_v2`);
      if (c) { const { hdrs } = JSON.parse(c); return hdrs || []; }
    } catch (_) {}
    return [];
  });
  // isLoading is false from the start when cached data is already available
  const [isLoading, setIsLoading] = useState(() => {
    try { return !localStorage.getItem(`table_${specialty}_v2`); }
    catch (_) { return true; }
  });

  const [error, setError] = useState("");
  const { user, userCountry, userProfile, refreshUserData, hasSpecialtyAccess, addSpecialtyAccess } = useAuth();
  const userCriteria = userProfile;
  // Check if user has access to this specific specialty
  const hasCurrentSpecialtyAccess = hasSpecialtyAccess(specialty);
  const tableContainerRef = React.useRef(null);
  const loadingProgress = 0;
  // Firestore pagination state
  const [lastDoc, setLastDoc] = useState(null);
  const [allLoaded, setAllLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const lastDocRef = React.useRef(null);
  const allLoadedRef = React.useRef(false);
  const loadingMoreRef = React.useRef(false);
  const loadMoreRef = React.useRef(null);
  const sentinelRef = React.useRef(null);
  const PAGE_SIZE = 20;
  const specialtyKey = specialty?.toLowerCase().replace(/\s+/g, "_");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [favorites, setFavorites] = useState({});
  const [favoritesLoading, setFavoritesLoading] = useState({});

  const [selectedPaymentCountry, setSelectedPaymentCountry] = useState("India");
  const [showCurrencySelector, setShowCurrencySelector] = useState(false);
  
  // Updated filter states - using selectedFilters instead of activeFilters
  const [showFilterModal, setShowFilterModal] = useState(false);
const [selectedFilters, setSelectedFilters] = useState({
  state: "",
  graduation: "",
  step1: "",
  step2: "",
  usce: "",
  visa: ""
});
   const getPopularCountriesForPayment = () => {
  return [
    // Most Popular (Top tier)
    { country: "India", currency: "INR", flag: "🇮🇳" },
    { country: "United States", currency: "USD", flag: "🇺🇸" },
    { country: "United Kingdom", currency: "GBP", flag: "🇬🇧" },
    { country: "Canada", currency: "CAD", flag: "🇨🇦" },
    { country: "Australia", currency: "AUD", flag: "🇦🇺" },
    
    // European Countries
    { country: "Germany", currency: "EUR", flag: "🇩🇪" },
    { country: "France", currency: "EUR", flag: "🇫🇷" },
    { country: "Spain", currency: "EUR", flag: "🇪🇸" },
    { country: "Italy", currency: "EUR", flag: "🇮🇹" },
    { country: "Netherlands", currency: "EUR", flag: "🇳🇱" },
    { country: "Switzerland", currency: "CHF", flag: "🇨🇭" },
    { country: "Sweden", currency: "SEK", flag: "🇸🇪" },
    { country: "Norway", currency: "NOK", flag: "🇳🇴" },
    { country: "Denmark", currency: "DKK", flag: "🇩🇰" },
    
    // Asian Countries
    { country: "Singapore", currency: "SGD", flag: "🇸🇬" },
    { country: "Malaysia", currency: "MYR", flag: "🇲🇾" },
    { country: "Thailand", currency: "THB", flag: "🇹🇭" },
    { country: "Philippines", currency: "PHP", flag: "🇵🇭" },
    { country: "Indonesia", currency: "IDR", flag: "🇮🇩" },
    { country: "Vietnam", currency: "VND", flag: "🇻🇳" },
    { country: "Japan", currency: "JPY", flag: "🇯🇵" },
    { country: "South Korea", currency: "KRW", flag: "🇰🇷" },
    { country: "China", currency: "CNY", flag: "🇨🇳" },
    { country: "Hong Kong", currency: "HKD", flag: "🇭🇰" },
    { country: "Taiwan", currency: "TWD", flag: "🇹🇼" },
    
    // South Asian Countries
    { country: "Pakistan", currency: "PKR", flag: "🇵🇰" },
    { country: "Bangladesh", currency: "BDT", flag: "🇧🇩" },
    { country: "Sri Lanka", currency: "LKR", flag: "🇱🇰" },
    { country: "Nepal", currency: "NPR", flag: "🇳🇵" },
    
    // Middle Eastern Countries
    { country: "Saudi Arabia", currency: "SAR", flag: "🇸🇦" },
    { country: "United Arab Emirates", currency: "AED", flag: "🇦🇪" },
    { country: "Qatar", currency: "QAR", flag: "🇶🇦" },
    { country: "Kuwait", currency: "KWD", flag: "🇰🇼" },
    { country: "Bahrain", currency: "BHD", flag: "🇧🇭" },
    { country: "Oman", currency: "OMR", flag: "🇴🇲" },
    { country: "Jordan", currency: "JOD", flag: "🇯🇴" },
    { country: "Israel", currency: "ILS", flag: "🇮🇱" },
    
    // African Countries
    { country: "South Africa", currency: "ZAR", flag: "🇿🇦" },
    { country: "Nigeria", currency: "NGN", flag: "🇳🇬" },
    { country: "Kenya", currency: "KES", flag: "🇰🇪" },
    { country: "Egypt", currency: "EGP", flag: "🇪🇬" },
    { country: "Ghana", currency: "GHS", flag: "🇬🇭" },
    { country: "Ethiopia", currency: "ETB", flag: "🇪🇹" },
    { country: "Morocco", currency: "MAD", flag: "🇲🇦" },
    
    // Latin American Countries
    { country: "Brazil", currency: "BRL", flag: "🇧🇷" },
    { country: "Mexico", currency: "MXN", flag: "🇲🇽" },
    { country: "Argentina", currency: "ARS", flag: "🇦🇷" },
    { country: "Chile", currency: "CLP", flag: "🇨🇱" },
    { country: "Colombia", currency: "COP", flag: "🇨🇴" },
    
    // Eastern European Countries
    { country: "Poland", currency: "PLN", flag: "🇵🇱" },
    { country: "Czech Republic", currency: "CZK", flag: "🇨🇿" },
    { country: "Hungary", currency: "HUF", flag: "🇭🇺" },
    { country: "Romania", currency: "RON", flag: "🇷🇴" },
    { country: "Russia", currency: "RUB", flag: "🇷🇺" },
    { country: "Turkey", currency: "TRY", flag: "🇹🇷" },
    { country: "Ukraine", currency: "UAH", flag: "🇺🇦" },
  ];
};

  // Helper function to clean percentage values
  const cleanPercentage = (value) => {
    if (!value) return 0;
    // Remove % symbol if it exists and convert to number
    const cleanValue = value.toString().replace('%', '');
    return parseFloat(cleanValue) || 0;
  };

  // New compatibility algorithm based on %NON US IMGs × Positions with 5% randomization
 // New compatibility algorithm based on %NON US IMGs × Positions with 5% randomization
const calculateFamiliarityScore = React.useCallback((collegeData, userId) => {
  const nonUSImgPercent = cleanPercentage(
    collegeData['%NON US IMGS'] || 
    collegeData['% NON US IMGS'] ||
    collegeData['%non-US IMG'] || 
    collegeData['% non-US IMG'] ||
    collegeData['%non us imgs'] ||
    collegeData['% non us imgs'] ||
    0
  );
  const positions = parseInt(collegeData['POSITIONS'] || 0);
  
  // If no data available, return minimum score with some variation
  if (nonUSImgPercent === 0 || positions === 0) {
    // Create deterministic variation for 0% IMG programs using user ID + program name
    const seedString = `${userId}_${collegeData['Program Name'] || collegeData['PROGRAM NAME'] || ''}_nodata`;
    let hash = 0;
    for (let i = 0; i < seedString.length; i++) {
      const char = seedString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    const variation = ((Math.abs(hash) % 1000) / 1000) * 2; // 0-2% variation
    return Math.min(Math.max(3 + variation, 3), 5); // Strictly enforce 3-5% for 0% programs
  }

  const baseScore = (nonUSImgPercent / 100) * positions;
  
  const seedString = `${userId}_${collegeData['Program Name'] || collegeData['PROGRAM NAME'] || ''}`;
  let hash = 0;
  for (let i = 0; i < seedString.length; i++) {
    const char = seedString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  const randomFactor = ((Math.abs(hash) % 1000) / 1000) * 10 - 5; // -5 to +5
  const finalScore = baseScore * (1 + randomFactor / 100);
  
  // Enhanced scaling with stricter bounds
  // Use a more conservative logarithmic scaling
  let scaledScore;
  if (finalScore <= 1) {
    // Very low scores: 3-15% range
    scaledScore = 3 + (finalScore * 12);
  } else if (finalScore <= 10) {
    // Medium scores: 15-60% range  
    scaledScore = 15 + ((finalScore - 1) / 9) * 45;
  } else {
    // High scores: 60-90% range (capped)
    scaledScore = 60 + Math.min(((finalScore - 10) / 40) * 30, 30);
  }
  
  // STRICT enforcement of bounds: minimum 3%, maximum 90%
  const result = Math.min(Math.max(scaledScore, 3), 90);
  
  // Additional safety check - log if we're hitting edge cases
  if (result >= 90) {
    console.warn(`High familiarity score capped at 90% for program: ${collegeData['Program Name'] || collegeData['PROGRAM NAME']}`);
  }
  
  return result;
}, []);

  // Fetch user's favorites
  const fetchFavorites = React.useCallback(async () => {
    if (!auth.currentUser) return;
    
    try {
      const favoritesRef = doc(db, "favorites", auth.currentUser.uid);
      const favoritesSnap = await getDoc(favoritesRef);
      
      if (favoritesSnap.exists()) {
        const data = favoritesSnap.data();
        const { createdAt, updatedAt, ...specialtyData } = data;
        setFavorites(specialtyData);
      }
    } catch (error) {
      console.error("Error fetching favorites:", error);
    }
  }, []);

  // Check if a program is favorited
  const isProgramFavorited = React.useCallback((program) => {
    const specialtyFavorites = favorites[specialty] || [];
    const programName = program['Program Name'] || program['PROGRAM NAME'] || '';
    return specialtyFavorites.some(fav => 
      (fav['Program Name'] === programName) || (fav['PROGRAM NAME'] === programName)
    );
  }, [favorites, specialty]);

  // Toggle favorite status
  const toggleFavorite = async (program, event) => {
    event.stopPropagation(); // Prevent row click
    
    if (!auth.currentUser || !specialty) return;
    
    const programKey = program['Program Name'] || program['PROGRAM NAME'] || '';
    setFavoritesLoading(prev => ({ ...prev, [programKey]: true }));
    
    try {
      const favoritesRef = doc(db, "favorites", auth.currentUser.uid);
      const favoritesSnap = await getDoc(favoritesRef);
      
      const favoriteItem = {
        ...program,
        specialty: specialty,
        addedAt: new Date().toISOString(),
        familiarityScore: program.familiarityScore || 0
      };
      
      // Clean the favorite item
      const cleanedFavoriteItem = {};
      Object.keys(favoriteItem).forEach(key => {
        const value = favoriteItem[key];
        if (value !== null && value !== undefined && value !== '' && value !== '—') {
          cleanedFavoriteItem[key] = value;
        }
      });
      
      if (favoritesSnap.exists()) {
        const currentData = favoritesSnap.data();
        const specialtyFavorites = currentData[specialty] || [];
        
        const isCurrentlyFavorite = specialtyFavorites.some(
          fav => (fav['Program Name'] === programKey) || (fav['PROGRAM NAME'] === programKey)
        );
        
        if (isCurrentlyFavorite) {
          // Remove from favorites
          const updatedFavorites = specialtyFavorites.filter(
            fav => (fav['Program Name'] !== programKey) && (fav['PROGRAM NAME'] !== programKey)
          );
          
          await updateDoc(favoritesRef, {
            [specialty]: updatedFavorites,
            updatedAt: new Date().toISOString()
          });
          
          // Update local state
          setFavorites(prev => ({
            ...prev,
            [specialty]: updatedFavorites
          }));
        } else {
          // Add to favorites
          const updatedFavorites = [...specialtyFavorites, cleanedFavoriteItem];
          
          await updateDoc(favoritesRef, {
            [specialty]: updatedFavorites,
            updatedAt: new Date().toISOString()
          });
          
          // Update local state
          setFavorites(prev => ({
            ...prev,
            [specialty]: updatedFavorites
          }));
        }
      } else {
        // Create new favorites document
        await setDoc(favoritesRef, {
          [specialty]: [cleanedFavoriteItem],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        
        // Update local state
        setFavorites(prev => ({
          ...prev,
          [specialty]: [cleanedFavoriteItem]
        }));
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      alert("❌ Failed to update favorites. Please try again.");
    } finally {
      setFavoritesLoading(prev => ({ ...prev, [programKey]: false }));
    }
  };

  // Updated filter function to use selectedFilters
  const applyFilters = React.useCallback((data) => {
    if (Object.values(selectedFilters).every(filter => !filter)) {
      return data;
    }

    return data.filter(program => {
      let passesFilter = true;

      // Country filter - check if program accepts international graduates
      if (selectedFilters.country) {
        const programText = Object.values(program).join(' ').toLowerCase();
        const selectedCountry = selectedFilters.country.toLowerCase();
        
        // For non-US countries, look for IMG-friendly indicators
// For non-US countries, look for IMG-friendly indicators
        if (selectedCountry !== 'united states') {
          const isImgFriendly = programText.includes('img') || 
                               programText.includes('international') ||
                               programText.includes('foreign') ||
                               (program['%NON US IMGS'] && cleanPercentage(program['%NON US IMGS']) > 0) ||
                               (program['% NON US IMGS'] && cleanPercentage(program['% NON US IMGS']) > 0);
          
          if (!isImgFriendly) passesFilter = false;
        }
      }

      // State filter - check if program is in the selected state
      if (selectedFilters.state) {
        const programState = program['State'] || program['STATE'] || program['state'] || '';
        const selectedState = selectedFilters.state;
        
        // Direct match with the state abbreviation
        if (programState.toString().toUpperCase() !== selectedState.toUpperCase()) {
          passesFilter = false;
        }
      }

      // Graduation time filter
      if (selectedFilters.graduation) {
        // This would need program-specific graduation requirements
        // For now, we'll assume all programs accept recent graduates
      }

      // Step 1 filter
      if (selectedFilters.step1) {
        // This would need program-specific Step 1 requirements
        // For now, we'll assume programs accept various Step 1 statuses
      }

      // Step 2 filter (only works for paid users)
      if (selectedFilters.step2 && hasCurrentSpecialtyAccess) {
        const selectedRange = selectedFilters.step2;
        if (selectedRange !== "Not passed") {
          const [minScore] = selectedRange.split('-').map(s => parseInt(s));
          const requiredStep2 = program['INV STEP 2'] || program['Step 2 Cutoff'] || program['STEP 2 CUTOFF'] || program['Step2 Cutoff'];
          
          if (requiredStep2) {
            const cutoff = parseInt(requiredStep2.toString().replace(/\D/g, ''));
            if (minScore < cutoff) passesFilter = false;
          }
        }
      }

      // USCE filter
      if (selectedFilters.usce) {
        // Look for USCE requirements in program data
        const programText = Object.values(program).join(' ').toLowerCase();
        const requiresUSCE = programText.includes('usce required') || 
                            programText.includes('us clinical experience required');
        
        if (requiresUSCE && selectedFilters.usce === 'None') {
          passesFilter = false;
        }
      }

      // Visa filter
      if (selectedFilters.visa) {
        const visaColumn = program['Visa'] || program['VISA'] || program['visa'] || '';
        const visaText = visaColumn.toString().toLowerCase();
        
        if (selectedFilters.visa === 'J1') {
          // Only show programs that support J1 ONLY (not H1)
          const supportsJ1 = visaText.includes('j1') || visaText.includes('j-1');
          const supportsH1B = visaText.includes('h1b') || visaText.includes('h-1b') || visaText.includes('h1');
          if (!supportsJ1 || supportsH1B) passesFilter = false;
        } else if (selectedFilters.visa === 'H1') {
          // Only show programs that support H1 ONLY (not J1)
          const supportsJ1 = visaText.includes('j1') || visaText.includes('j-1');
          const supportsH1B = visaText.includes('h1b') || visaText.includes('h-1b') || visaText.includes('h1');
          if (!supportsH1B || supportsJ1) passesFilter = false;
        } else if (selectedFilters.visa === 'J1 or H1') {
          // Only show programs that support BOTH J1 AND H1
          const supportsJ1 = visaText.includes('j1') || visaText.includes('j-1');
          const supportsH1B = visaText.includes('h1b') || visaText.includes('h-1b') || visaText.includes('h1');
          if (!(supportsJ1 && supportsH1B)) passesFilter = false;
        } else if (selectedFilters.visa === 'No need') {
          // User doesn't need visa sponsorship, so all programs are acceptable
        }
      }

      return passesFilter;
    });
  }, [selectedFilters, hasCurrentSpecialtyAccess]);

  // Filter configuration function
const getFilterConfig = () => {
  console.log('=== FILTER DEBUG START ===');
  
  // Test FILTER_OPTIONS directly
  console.log('FILTER_OPTIONS object:', FILTER_OPTIONS);
  console.log('States array length:', FILTER_OPTIONS?.states?.length);
  console.log('First 3 states:', FILTER_OPTIONS?.states?.slice(0, 3));
  
  // Create filters array manually to test (REMOVED COUNTRY FILTER)
  const allFilters = [
    {
      key: 'state',
      icon: '🏛️',
      label: 'State',
      options: FILTER_OPTIONS?.states || ['AL', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI'], // Hardcode first 10 states for testing
      userValue: userCriteria?.state,
      color: 'blue',
      description: 'Filter by US state where the program is located'
    },
    {
      key: 'graduation', 
      icon: '🎓',
      label: 'Time from Graduation',
      options: FILTER_OPTIONS?.graduationTimes || ['<1 year', '1–2 years', '3–5 years', '>5 years'],
      userValue: userCriteria?.graduationTime,
      color: 'teal',
      description: 'Filter by time since graduation'
    },
    {
      key: 'step1',
      icon: '📝', 
      label: 'USMLE Step 1 Status',
      options: FILTER_OPTIONS?.step1Options || ['Pass in 1st attempt', 'Pass in 2nd attempt', 'Pass in 3rd attempt', 'Fail'],
      userValue: userCriteria?.step1,
      color: 'indigo',
      description: 'Filter by Step 1 attempt status'
    },
    {
      key: 'step2',
      icon: '📊',
      label: 'USMLE Step 2 Score',
      options: FILTER_OPTIONS?.step2Ranges || ['Not passed', '209-214', '215-219'],
      userValue: userCriteria?.step2,
      color: 'purple', 
      description: 'Filter by Step 2 CK score range',
      isPremium: true
    },
    {
      key: 'usce',
      icon: '🏥',
      label: 'US Clinical Experience',
      options: FILTER_OPTIONS?.usceOptions || ['None', '<1 month', '1–3 months', '3+ months'],
      userValue: userCriteria?.usce,
      color: 'green',
      description: 'Filter by USCE experience level'
    },
    {
      key: 'visa',
      icon: '📄',
      label: 'Visa Sponsorship',
      options: FILTER_OPTIONS?.visaOptions || ['No need', 'J1', 'H1', 'J1 or H1'],
      userValue: userCriteria?.visa,
      color: 'orange',
      description: 'Filter by visa sponsorship requirements'
    }
  ];
    
    console.log('All filters created:', allFilters.length);
    console.log('Filter details:', allFilters.map(f => ({ 
      label: f.label, 
      optionsCount: f.options?.length || 0,
      hasOptions: (f.options?.length || 0) > 0,
      firstOption: f.options?.[0]
    })));
    
    // Filter out any without options
    const validFilters = allFilters.filter(f => f.options && f.options.length > 0);
    console.log('Valid filters:', validFilters.length);
    console.log('Valid filter labels:', validFilters.map(f => f.label));
    
    console.log('=== FILTER DEBUG END ===');
    
    return validFilters;
  };

  // ── Firestore paginated loader ──────────────────────────────────────────
  // Why Firestore instead of Storage CSV?
  // AdminUpload now writes every CSV row into Firestore subcollection
  // specialtyData/{key}/programs. The first 20 rows arrive in ~200-400 ms
  // (a Firestore index scan) instead of waiting for the full file download.
  // More rows auto-load as the user scrolls via IntersectionObserver.

  const buildFirestoreQuery = (afterDoc = null) => {
    const col = collection(db, "specialtyData", specialtyKey, "programs");
    const constraints = [orderBy("_idx"), limit(PAGE_SIZE)];
    if (afterDoc) constraints.push(startAfter(afterDoc));
    return query(col, ...constraints);
  };

  const rowFromDoc = (docSnapshot, uid) => {
    const data = { ...docSnapshot.data() };
    delete data._idx;
    return { ...data, familiarityScore: uid ? calculateFamiliarityScore(data, uid) : 0 };
  };

  // Fallback: download full CSV from Storage (for specialties not yet in Firestore)
  const fetchCSVFallback = async (uid) => {
    try {
      const fileName = specialtyKey + ".csv";
      const fileRef = ref(storage, `specialties/${fileName}`);
      const url = await getDownloadURL(fileRef);
      const response = await fetch(url, { cache: 'default' });
      const csvText = await response.text();
      const parsed = Papa.parse(csvText, {
        header: true, skipEmptyLines: true,
        transformHeader: h => h.replace(/^\ufeff/, '').trim()
      });
      const allRows = parsed.data
        .filter(row => Object.values(row).some(cell => cell?.toString().trim()))
        .map(row => ({ ...row, familiarityScore: uid ? calculateFamiliarityScore(row, uid) : 0 }));
      setHeaders(parsed.meta.fields || []);
      setTableData(allRows);
      setAllLoaded(true);
      setIsLoading(false);
      try {
        localStorage.setItem(`table_${specialty}_v2`, JSON.stringify({
          data: allRows, hdrs: parsed.meta.fields || [], ts: Date.now()
        }));
      } catch (_) {}
    } catch (err) {
      setError("Failed to load data: " + err.message);
      setIsLoading(false);
    }
  };

  const loadInitial = async () => {
    const CACHE_KEY = `table_${specialty}_v2`;
    const FRESH_WINDOW = 5 * 60 * 1000;
    const uid = auth.currentUser?.uid;

    // Serve cache INSTANTLY — zero network on return visits
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, hdrs, ts } = JSON.parse(cached);
        setHeaders(hdrs);
        setTableData(data);
        setAllLoaded(true); // cache has everything
        setIsLoading(false);
        if (Date.now() - ts < FRESH_WINDOW) return; // very fresh — done
        // Stale cache: silently refresh in the background after returning
        refreshFromFirestore(uid);
        return;
      }
    } catch (_) { localStorage.removeItem(CACHE_KEY); }

    // No cache — first ever visit: query Firestore for page 1
    try {
      const snap = await getDocs(buildFirestoreQuery());
      if (snap.empty) {
        // Firestore collection not populated yet (old upload) → fall back to CSV
        await fetchCSVFallback(uid);
        return;
      }
      // Read column order from the meta document
      const metaDoc = await getDoc(doc(db, "specialtyData", specialtyKey, "meta", "fields"));
      const hdrs = metaDoc.exists()
        ? metaDoc.data().headers
        : Object.keys(snap.docs[0].data()).filter(k => k !== '_idx');

      const rows = snap.docs.map(d => rowFromDoc(d, uid));
      setHeaders(hdrs);
      setTableData(rows);
      const initialLastDoc = snap.docs[snap.docs.length - 1];
      lastDocRef.current = initialLastDoc;
      setLastDoc(initialLastDoc);
      const initialAllLoaded = snap.docs.length < PAGE_SIZE;
      allLoadedRef.current = initialAllLoaded;
      setAllLoaded(initialAllLoaded);
      setIsLoading(false);
    } catch (err) {
      setError("Failed to load data: " + err.message);
      setIsLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMoreRef.current || allLoadedRef.current || !lastDocRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const uid = auth.currentUser?.uid;
    try {
      const snap = await getDocs(buildFirestoreQuery(lastDocRef.current));
      if (!snap.empty) {
        const newRows = snap.docs.map(d => rowFromDoc(d, uid));
        setTableData(prev => {
          const merged = [...prev, ...newRows];
          try {
            const cached = localStorage.getItem(`table_${specialty}_v2`);
            const hdrs = cached ? JSON.parse(cached).hdrs : headers;
            localStorage.setItem(`table_${specialty}_v2`, JSON.stringify({
              data: merged, hdrs, ts: Date.now()
            }));
          } catch (_) {}
          return merged;
        });
        const newLastDoc = snap.docs[snap.docs.length - 1];
        lastDocRef.current = newLastDoc;
        setLastDoc(newLastDoc);
        if (snap.docs.length < PAGE_SIZE) { allLoadedRef.current = true; setAllLoaded(true); }
      } else {
        allLoadedRef.current = true;
        setAllLoaded(true);
      }
    } catch (_) {}
    loadingMoreRef.current = false;
    setLoadingMore(false);
  };

  // Background revalidation: silently replace stale cached data
  const refreshFromFirestore = async (uid) => {
    try {
      const allRows = [];
      let afterDoc = null;
      const metaDoc = await getDoc(doc(db, "specialtyData", specialtyKey, "meta", "fields"));
      while (true) {
        const snap = await getDocs(buildFirestoreQuery(afterDoc));
        if (snap.empty) break;
        allRows.push(...snap.docs.map(d => rowFromDoc(d, uid)));
        afterDoc = snap.docs[snap.docs.length - 1];
        if (snap.docs.length < PAGE_SIZE) break;
      }
      if (allRows.length === 0) return;
      const hdrs = metaDoc.exists()
        ? metaDoc.data().headers
        : Object.keys(allRows[0]).filter(k => k !== 'familiarityScore');
      setHeaders(hdrs);
      setTableData(allRows);
      setAllLoaded(true);
      try {
        localStorage.setItem(`table_${specialty}_v2`, JSON.stringify({ data: allRows, hdrs, ts: Date.now() }));
      } catch (_) {}
    } catch (_) {}
  };

  // Keep refs in sync with state so the IntersectionObserver callback
  // always reads the latest values without needing to re-create the observer.
  useEffect(() => { lastDocRef.current = lastDoc; }, [lastDoc]);
  useEffect(() => { allLoadedRef.current = allLoaded; }, [allLoaded]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);
  // Always point to the latest loadMore so the observer never uses a stale closure
  useEffect(() => { loadMoreRef.current = loadMore; });

  // After the initial batch loads, if the sentinel is already visible inside the container
  // (20 rows don't fill the viewport), the observer won't re-fire. Manually kick loadMore.
  useEffect(() => {
    if (!lastDoc || allLoaded) return;
    const container = tableContainerRef.current;
    const sentinel = sentinelRef.current;
    if (!container || !sentinel) return;
    // If content doesn't overflow the container, sentinel is already in view — load more
    if (container.scrollHeight <= container.clientHeight + 5) {
      loadMoreRef.current?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastDoc]);

  // IntersectionObserver: fire loadMore when the sentinel div enters the viewport.
  // root = tableContainerRef so intersection is relative to the scrollable table container.
  useEffect(() => {
    if (!sentinelRef.current || !tableContainerRef.current) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        loadMoreRef.current?.();
      }
    }, { root: tableContainerRef.current, threshold: 0, rootMargin: '200px' });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // empty deps — intentional, uses refs

  useEffect(() => {
  const initializePage = async () => {
    const hasCached = (() => {
      try { return !!localStorage.getItem(`table_${specialty}_v2`); } catch (_) { return false; }
    })();
    if (!hasCached) {
      setTableData([]);
      setHeaders([]);
      setIsLoading(true);
    }
    setLastDoc(null);
    setAllLoaded(false);
    setError("");

    initializeExchangeRates().catch(() => {});
    await loadInitial();
    if (user) fetchFavorites();
  };

  initializePage();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [specialty, user]);
  // Add this debug useEffect after fetchCSV
  useEffect(() => {
    console.log('🔍 Debug - Component state:', {
      isLoading,
      hasData: tableData.length > 0,
      hasHeaders: headers.length > 0,
      hasError: !!error
    });
  }, [isLoading, tableData.length, headers.length, error]);

useEffect(() => {
  const handlePageShow = (event) => {
    if (event.persisted || window.performance.navigation.type === 2) {
      console.log('Page loaded from cache, refreshing user data...');
      if (user) {
        refreshUserData(true);
      }
    }
  };

  window.addEventListener('pageshow', handlePageShow);
  return () => window.removeEventListener('pageshow', handlePageShow);
}, [user, refreshUserData]);
// Add this useEffect near the end of your component, with your other useEffect hooks
useEffect(() => {
  const handleResize = () => {};

  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);
  // Updated isBlurred function to check for STEP 2 CUTOFF and %NON US IMGs
const isBlurred = (colName) => {
  const lower = colName.toLowerCase();
  return !hasCurrentSpecialtyAccess && (
    // Original locked columns
    lower.includes("step 2 cutoff") || 
    lower.includes("step2 cutoff") ||
    lower === "step 2 cutoff" ||
    lower === "step2 cutoff" ||
    // New locked columns
    lower.includes("%non us imgs") ||
    lower.includes("% non us imgs") ||
    lower.includes("%non-us img") ||
    lower.includes("% non-us img") ||
    lower === "%non us imgs" ||
    lower === "% non us imgs" ||
    lower.includes("inv step 2") ||
    lower === "inv step 2"
  );
};

  // Check if familiarity score should be locked
const isFamiliarityLocked = () => {
  return !hasCurrentSpecialtyAccess;
};

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleRowClick = (rowData) => {
    // Detect the program name column: prefer "Program Name" variants, then exact "NAME"
    const programNameKey =
      (headers || []).find((h) => /program\s*name/i.test(String(h).trim())) ||
      (headers || []).find((h) => /^name$/i.test(String(h).trim())) ||
      undefined;
    navigate(`/compare/${specialty}`, {
      state: { collegeData: rowData, specialty: specialty, programNameKey }
    });
  };

  // Handle locked column click - prevent row click and show payment modal
  const handleLockedColumnClick = (event) => {
    event.stopPropagation(); // Prevent row click event
    setShowPaymentModal(true);
  };

// Check if column should be hidden from table view
const isHiddenColumn = (colName) => {
  const lower = colName.toLowerCase().trim();
  return lower === "type" || 
         lower === "city" ||
         lower === "region" ||
         lower === "inv  ss" ||    // 2 spaces
         lower === "inv  gs" ||    // 2 spaces  
         lower === "inv  ns" ||    // 2 spaces
         lower === "inv ss" ||     // 1 space (backup)
         lower === "inv gs" ||     // 1 space (backup)
         lower === "inv ns";       // 1 space (backup)
};
  // Check if column is a website/residents link column
  const isWebsiteColumn = (colName) => {
    const lower = colName.toLowerCase();
    return lower === "residents" ||
           lower.includes("website") ||
           lower.includes("residents/program website") ||
           lower.includes("program website");
  };

  // Extract first URL from cell value for "View Residents" button
  const extractUrlFromCell = (text) => {
    if (!text || text === "—" || text === "" || text === "N/A") return null;
    const urlRegex = /(https?:\/\/[^\s]+)/;
    const withProtocol = String(text).match(urlRegex);
    if (withProtocol) return withProtocol[1];
    const wwwRegex = /(www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/;
    const withoutProtocol = String(text).match(wwwRegex);
    if (withoutProtocol) return `https://${withoutProtocol[1]}`;
    return null;
  };

  // Handle website column click - prevent row click
  const handleWebsiteColumnClick = (event) => {
    event.stopPropagation(); // Prevent row click event
    // Let the link clicks handle their own navigation
  };

  const getModifiedHeaders = () => {
    if (!headers.length) return [];
    
    let modifiedHeaders = [...headers];
    
    // Add familiarity score for authenticated users or when filters are active
    if (auth.currentUser || Object.values(selectedFilters).some(f => f)) {
      modifiedHeaders = ['Familiarity Score', ...modifiedHeaders];
    }
    
    // Add favorites column for authenticated users
    if (auth.currentUser) {
      modifiedHeaders = ['Favorite', ...modifiedHeaders];
    }
    
    return modifiedHeaders;
  };

  // Filter headers to exclude hidden columns
  const visibleHeaders = getModifiedHeaders().filter(header => !isHiddenColumn(header));

  // Helper function to shuffle array with consistent seed for each user
  const shuffleArrayWithSeed = React.useCallback((array, seed) => {
    // Safety check
    if (!Array.isArray(array) || array.length === 0) {
      return array;
    }
    
    // Filter out any undefined/null entries before shuffling
    const validArray = array.filter(item => item != null);
    const shuffled = [...validArray];
    
    if (shuffled.length === 0) {
      return [];
    }
    
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Fisher-Yates shuffle with seeded random
    for (let i = shuffled.length - 1; i > 0; i--) {
      hash = (hash * 9301 + 49297) % 233280; // Linear congruential generator
      const randomValue = hash / 233280;
      const j = Math.floor(randomValue * (i + 1));
      
      // Ensure both indices are valid
      if (j >= 0 && j < shuffled.length && i >= 0 && i < shuffled.length) {
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
    }
    
    return shuffled;
  }, []);

  // Helper function to add controlled randomization while preserving score tiers
  const addRandomizationToSortedList = React.useCallback((sortedArray, userSeed) => {
    // Safety check - ensure we have a valid array
    if (!Array.isArray(sortedArray) || sortedArray.length === 0) {
      return sortedArray;
    }

    // Group programs by score tiers to maintain relative ordering
    const tiers = {
      excellent: [], // 80-100%
      good: [],      // 60-79%
      fair: [],      // 40-59%
      low: []        // 0-39%
    };
    
    sortedArray.forEach(program => {
      // Safety check - ensure program exists and has familiarityScore
      if (!program || typeof program !== 'object') {
        tiers.low.push(program);
        return;
      }
      
      const score = program.familiarityScore || 0;
      if (score >= 80) tiers.excellent.push(program);
      else if (score >= 60) tiers.good.push(program);
      else if (score >= 40) tiers.fair.push(program);
      else tiers.low.push(program);
    });
    
    // Shuffle within each tier
    const shuffledTiers = Object.keys(tiers).map(tier => 
      shuffleArrayWithSeed(tiers[tier], `${userSeed}_${tier}`)
    );
    
    // Combine tiers back together
    return shuffledTiers.flat();
  }, [shuffleArrayWithSeed]);

  const sortedData = React.useMemo(() => {
    let sortableData = [...tableData];
    
    // Default sorting logic based on payment status and filters
    if (sortConfig.key === null) {
      if (hasCurrentSpecialtyAccess && auth.currentUser){
        // Paid users with familiarity scores: sort by familiarity score (descending) + randomization
        sortableData.sort((a, b) => {
          const aScore = a.familiarityScore || 0;
          const bScore = b.familiarityScore || 0;
          return bScore - aScore; // Descending order (highest first)
        });
        
        // Add randomization while preserving general score ordering
        sortableData = addRandomizationToSortedList(sortableData, auth.currentUser.uid);
        
      } else if (Object.values(selectedFilters).some(f => f)) {
        // When filters are active: sort by familiarity score + randomization
        sortableData.sort((a, b) => {
          const aScore = a.familiarityScore || 0;
          const bScore = b.familiarityScore || 0;
          return bScore - aScore; // Descending order (highest first)
        });
        
        // Add randomization for filtered results too
        const userSeed = auth.currentUser?.uid || 'anonymous';
        sortableData = addRandomizationToSortedList(sortableData, userSeed);
        
      } else {
        // Default for unpaid users or no filters: sort alphabetically by Program Name
        sortableData.sort((a, b) => {
          const aName = a['Program Name'] || '';
          const bName = b['Program Name'] || '';
          return aName.localeCompare(bName); // Alphabetical order
        });
      }
    } else {
      // Manual sorting when user clicks on column headers (keep existing logic)
      sortableData.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        // Special handling for familiarity score
        if (sortConfig.key === 'Familiarity Score') {
          aVal = a.familiarityScore || 0;
          bVal = b.familiarityScore || 0;
        }
        
        // Convert to numbers for numeric sorting
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
        
        // String comparison
        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [tableData, sortConfig, hasCurrentSpecialtyAccess, selectedFilters, addRandomizationToSortedList]);

  // Apply filters to sorted data
  const filteredAndSortedData = React.useMemo(() => {
    return applyFilters(sortedData);
  }, [sortedData, applyFilters]);

  const filteredData = filteredAndSortedData; // Single list - no grouping needed

  // Get pricing and currency based on user's country using constants
  const getPricingInfo = () => {
    const pricingData = getSpecialtyPricingInfo(specialty, selectedPaymentCountry);
    const currencySymbol = getSymbolFromCurrency(pricingData.currency) || "$";
    
    // Use the new getPaymentAmount function for proper Razorpay conversion
    const razorpayAmount = getPaymentAmount(pricingData.amount, pricingData.currency);
    
    return { 
      amount: pricingData.amount, 
      currency: pricingData.currency, 
      razorpayAmount, 
      currencySymbol,
      formattedAmount: formatCurrency(pricingData.amount, pricingData.currency)
    };
  };

// Replace your existing handlePayment function with this enhanced version
const handlePayment = async () => {
  if (paymentLoading) return;
  setPaymentLoading(true);

  const res = await loadRazorpayScript();
  if (!res) {
    alert("Payment system failed to load. Please check your internet connection.");
    setPaymentLoading(false);
    return;
  }

  const { amount, currency, razorpayAmount, currencySymbol } = getPricingInfo();

try {
    // Step 1: Fetch Razorpay key from backend
    const keyResponse = await fetch(`${BACKEND_URL}/api/razorpay-key`);
    if (!keyResponse.ok) {
      throw new Error('Failed to fetch Razorpay key');
    }
    const { key: razorpayKey } = await keyResponse.json();

    // Step 2: Create order from backend
    console.log('Creating order with:', { razorpayAmount, currency, specialty });

    const orderResponse = await fetch(`${BACKEND_URL}/api/create-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        amount: razorpayAmount,
        currency: currency,
        specialty: specialty,
        userId: auth.currentUser?.uid
      })
    });

    if (!orderResponse.ok) {
      const errorText = await orderResponse.text();
      throw new Error(`Failed to create order: ${orderResponse.status} - ${errorText}`);
    }

    const order = await orderResponse.json();
    console.log('Order created successfully:', order);

    // Validate order response
    if (!order.id) {
      throw new Error('Invalid order response: missing order ID');
    }

    const options = {
      key: razorpayKey, 
      amount: order.amount,
      currency: order.currency,
      order_id: order.id, // THIS IS THE CRITICAL ADDITION
      name: "Residency Companion",
      description: "Unlock Premium Features",
      
      modal: {
        backdrop_close: false,
        escape: true,
        animation: true,
        confirm_close: true,
        
        ondismiss: function() {
          setPaymentLoading(false);
          console.log('Razorpay modal dismissed');
        },
        
      },
      
      handler: async function (response) {
        try {
          // Step 3: Verify payment on backend
          const verificationResponse = await fetch(`${BACKEND_URL}/api/verify-payment`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            })
          });

          const verificationResult = await verificationResponse.json();

          if (verificationResult.success) {
            // Payment is auto-captured by Razorpay, proceed to update user access
            console.log('Payment verified and captured:', response.razorpay_payment_id);

            // Step 4: Update user access in Firestore
            const uid = auth.currentUser?.uid;
            if (!uid) {
              setPaymentLoading(false);
              return;
            }

            // Add specialty-specific access
            await addSpecialtyAccess(specialty, {
              paymentId: response.razorpay_payment_id,
              amount: amount,
              currency: currency,
              country: selectedPaymentCountry
            });

            // Refresh user data to update UI
            await refreshUserData(true);
            setShowPaymentModal(false);
            setPaymentLoading(false);
            alert(`Payment successful! ${specialty} programs unlocked for ${currencySymbol}${amount}.`);
          } else {
            throw new Error('Payment verification failed');
          }

        } catch (err) {
          console.error("Error updating payment status:", err);
          setPaymentLoading(false);
          alert("Payment processed but failed to update account. Please contact support.");
        }
      },
      
      prefill: {
        name: auth.currentUser?.displayName || "User",
        email: auth.currentUser?.email,
      },
      
      theme: { 
        color: "#2563eb",
        backdrop_color: "rgba(0, 0, 0, 0.6)"
      },
      
      retry: {
        enabled: true,
        max_count: 3
      },
      
      remember_customer: false,
      timeout: 300,
      
      error: function(error) {
        console.error('Razorpay error:', error);
        setPaymentLoading(false);
        
        let errorMessage = "Payment failed. Please try again.";
        
        if (error.code === 'BAD_REQUEST_ERROR') {
          errorMessage = "Invalid payment details. Please check and try again.";
        } else if (error.code === 'NETWORK_ERROR') {
          errorMessage = "Network error. Please check your internet connection.";
        } else if (error.code === 'SERVER_ERROR') {
          errorMessage = "Server error. Please try again in a moment.";
        }
        
        alert(errorMessage);
      }
    };

    try {
      setShowPaymentModal(false);
      
      setTimeout(() => {
        const paymentObject = new window.Razorpay(options);
        paymentObject.open();
      }, 200);
      
    } catch (error) {
      console.error('Error opening Razorpay:', error);
      setPaymentLoading(false);
      alert("Failed to open payment window. Please try again.");
    }
    
  } catch (error) {
    console.error('Error in order creation:', error);
    setPaymentLoading(false);
    alert("Failed to create order. Please try again.");
  }
};

// Enhanced loadRazorpayScript function with better error handling
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    // Check if script is already loaded
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    
    // Remove any existing script to avoid conflicts
    const existingScript = document.querySelector('script[src*="checkout.razorpay.com"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    
    script.onload = () => {
      console.log('Razorpay script loaded successfully');
      resolve(true);
    };
    
    script.onerror = (error) => {
      console.error('Failed to load Razorpay script:', error);
      resolve(false);
    };
    
    // Add timeout for script loading
    const timeoutId = setTimeout(() => {
      console.error('Razorpay script loading timeout');
      resolve(false);
    }, 10000); // 10 second timeout
    
    script.onload = () => {
      clearTimeout(timeoutId);
      console.log('Razorpay script loaded successfully');
      resolve(true);
    };
    
    document.body.appendChild(script);
  });
};

  const lockedColumnsCount = headers.filter(header => isBlurred(header)).length + (isFamiliarityLocked() ? 1 : 0);
  // eslint-disable-next-line no-unused-vars
  const { formattedAmount } = getPricingInfo(); // Get formatted price for display

  // Format aggregate marks display
  const getAggregateMarksDisplay = () => {
    if (userCriteria?.obtainedMarks && userCriteria?.maximumMarks) {
      const percentage = userCriteria?.aggregatePercentage || 
        ((parseFloat(userCriteria.obtainedMarks) / parseFloat(userCriteria.maximumMarks)) * 100).toFixed(2);
      return `${userCriteria.obtainedMarks}/${userCriteria.maximumMarks} (${percentage}%)`;
    }
    return null;
  };

  // Criteria display configuration
  const getCriteriaDisplayItems = () => {
    if (!userCriteria) return [];
    
    const items = [
      {
        icon: "🌍",
        label: "Country",
        value: userCriteria.country,
        color: "blue"
      },
      {
        icon: "📚",
        label: "Aggregate",
        value: getAggregateMarksDisplay(),
        color: "emerald"
      },
      {
        icon: "🎓",
        label: "Graduation",
        value: userCriteria.graduationTime,
        color: "teal"
      },
      {
        icon: "📝",
        label: "Step 1",
        value: userCriteria.step1,
        color: "indigo"
      },
      {
        icon: "📊",
        label: "Step 2",
        value: userCriteria.step2,
        color: "purple"
      },
      {
        icon: "🏥",
        label: "USCE",
        value: userCriteria.usce,
        color: "green"
      },
      {
        icon: "📄",
        label: "Visa",
        value: userCriteria.visa,
        color: "orange"
      }
    ].filter(item => item.value && item.value !== "Not set");
    
    return items;
  };

  // Get familiarity score color based on value
  const getFamiliarityScoreColor = (score) => {
    if (score >= 80) return "text-black bg-black/10";
    if (score >= 60) return "text-gray-800 bg-black/[0.08]";
    if (score >= 40) return "text-gray-700 bg-black/[0.06]";
    if (score >= 20) return "text-gray-600 bg-black/[0.05]";
    return "text-gray-500 bg-black/[0.04]";
  };

  // Render cell content based on column type
  const renderCellContent = (col, row, colIndex) => {
    // Safety check - ensure row exists
    if (!row || typeof row !== 'object') {
      return <span className="text-gray-400">—</span>;
    }

    if (col === 'Favorite') {
      if (!auth.currentUser) return <span>—</span>;
      
      const programKey = row['Program Name'] || row['PROGRAM NAME'] || '';
      const isFavorite = isProgramFavorited(row);
      const isLoading = favoritesLoading[programKey];
      
      return (
        <button
          onClick={(e) => toggleFavorite(row, e)}
          disabled={isLoading}
          className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed ${
            isFavorite
              ? 'text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300'
              : 'text-gray-400 dark:text-gray-500 hover:text-black dark:hover:text-white'
          }`}
          title={isFavorite ? "Remove from favorites" : "Add to favorites"}
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-black dark:border-t-white rounded-full animate-spin"></div>
          ) : (
            <Heart size={20} filled={isFavorite} />
          )}
        </button>
      );
    }

    if (col === 'Familiarity Score') {
      // Check if familiarity score should be locked
      if (isFamiliarityLocked()) {
        return (
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-black/10 dark:bg-white/10 rounded-lg flex items-center justify-center">
              <svg className="w-3 h-3 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <span className="text-gray-600 dark:text-gray-400 font-medium">Unlock to view</span>
          </div>
        );
      }

      // Safety check for undefined row
      if (!row || typeof row !== 'object') {
        return <span>—</span>;
      }
      const score = row.familiarityScore || 0;
      return (
        <div className="flex items-center space-x-2">
          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${getFamiliarityScoreColor(score)}`}>
            {score.toFixed(1)}%
          </div>
          <div className="w-16 bg-black/10 dark:bg-white/20 rounded-full h-2">
            <div
              className="bg-black dark:bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(score, 100)}%` }}
            />
          </div>
        </div>
      );
    }

    // All other columns are from CSV data
    if (isBlurred(col)) {
      return (
        <div className="flex items-center space-x-2">
          <div className="w-6 h-6 bg-black/10 dark:bg-white/10 rounded-lg flex items-center justify-center">
            <svg className="w-3 h-3 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <span className="text-gray-600 dark:text-gray-400 font-medium">Unlock to view</span>
        </div>
      );
    }

    if (isWebsiteColumn(col)) {
      const url = extractUrlFromCell(row[col]);
      if (!url) return <span className="text-gray-400 dark:text-gray-500">—</span>;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-[13px] font-medium bg-black/8 dark:bg-white/10 hover:bg-black/12 dark:hover:bg-white/15 text-gray-800 dark:text-gray-200 border border-black/10 dark:border-gray-600 transition-colors"
        >
          View
        </a>
      );
    }

    // Regular CSV column data
    return (
      <span className="group-hover:text-black dark:group-hover:text-white transition-colors">
        {row[col] || "—"}
      </span>
    );
  };

  // Full-screen spinner removed — skeleton rows inside the table handle the
  // empty-data case so the user always sees layout immediately.

  if (error) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 px-4 sm:px-6 lg:px-8 py-12 flex flex-col items-center justify-center">
        <div className="w-16 h-16 bg-black/5 dark:bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-black dark:text-white mb-2">Data Not Available</h3>
        <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm text-center">{error}</p>
        <button
          onClick={() => navigate("/my-lists")}
          className="bg-gradient-to-r from-gray-800 to-black dark:from-gray-600 dark:to-gray-800 hover:from-gray-700 hover:to-gray-900 dark:hover:from-gray-500 dark:hover:to-gray-700 text-white px-5 py-3 rounded-full font-medium transition-all duration-200 active:scale-[0.98]"
        >
          Back to Specialties
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/my-lists")}
            className="p-2.5 hover:bg-black/[0.06] dark:hover:bg-white/10 rounded-xl transition-all duration-200 active:scale-95"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl lg:text-3xl font-semibold text-black dark:text-white tracking-[-0.02em]">{specialty} Programs</h1>
            <div className="flex items-center space-x-3 mt-1">
              <span className="text-[14px] text-gray-500 dark:text-gray-400">{filteredData.length} programs</span>
              <span className="text-gray-300 dark:text-gray-600">•</span>
              <span className="text-[14px] text-gray-500 dark:text-gray-400">{userCountry}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilterModal(true)}
            className="bg-gradient-to-r from-gray-800 to-black dark:from-gray-600 dark:to-gray-800 hover:from-gray-700 hover:to-gray-900 dark:hover:from-gray-500 dark:hover:to-gray-700 text-white px-5 py-3 rounded-full font-medium transition-all duration-200 active:scale-[0.98] shadow-lg shadow-black/10 dark:shadow-black/20 flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filter
            {Object.values(selectedFilters).some(f => f) && (
              <span className="ml-2 bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">
                {Object.values(selectedFilters).filter(f => f).length}
              </span>
            )}
          </button>

          {!hasCurrentSpecialtyAccess && lockedColumnsCount > 0 && (
            <div className="gradient-border-wrap shadow-lg shadow-black/10 rounded-full">
              <button
                onClick={() => setShowPaymentModal(true)}
                className="gradient-border-inner text-white px-5 py-3 w-full font-medium transition-all duration-200 active:scale-[0.98]"
              >
                Upgrade Now
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Active Filters Bar */}
      {Object.values(selectedFilters).some(f => f) && (
        <div className="bg-black/[0.02] dark:bg-white/5 rounded-2xl p-4 border border-black/8 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-black dark:text-white flex items-center">
              <svg className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Active Filters ({Object.values(selectedFilters).filter(f => f).length})
            </h3>
            <button
              onClick={() => setSelectedFilters({
                state: "",
                graduation: "",
                step1: "",
                step2: "",
                usce: "",
                visa: ""
              })}
              className="text-xs text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white font-medium"
            >
              Clear All
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {getFilterConfig().map((filter, index) => {
              const selectedValue = selectedFilters[filter.key];
              if (!selectedValue) return null;

              return (
                <div
                  key={index}
                  className="inline-flex items-center bg-white dark:bg-gray-700 border border-black/10 dark:border-gray-600 rounded-full px-3 py-1.5 text-sm"
                >
                  <span className="mr-1.5">{filter.icon}</span>
                  <span className="text-gray-700 dark:text-gray-200 font-medium mr-2">{filter.label}: {selectedValue}</span>
                  <button
                    onClick={() => setSelectedFilters(prev => ({ ...prev, [filter.key]: "" }))}
                    className="text-gray-400 dark:text-gray-500 hover:text-black dark:hover:text-white ml-1"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-[13px] text-gray-500 dark:text-gray-400">
            Showing {filteredData.length} programs that match your criteria
          </div>
        </div>
      )}

      {/* User Profile - Simplified */}
      {userCriteria && getCriteriaDisplayItems().length > 0 && (
        <div className="bg-black/[0.02] dark:bg-white/5 rounded-2xl p-4 border border-black/8 dark:border-gray-700">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center space-x-3 flex-wrap">
              <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="text-black dark:text-white font-medium text-sm">Profile:</span>
              <div className="flex flex-wrap items-center gap-2">
                {getCriteriaDisplayItems().map((item, index) => (
                  <span key={index} className="bg-white dark:bg-gray-700 border border-black/10 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1 rounded-full text-[13px] font-medium">
                    {item.icon} {item.label}: {item.value}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={() => navigate("/setup-profile")}
              className="text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white font-medium text-sm"
            >
              Edit
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-black/8 dark:border-gray-700 overflow-hidden">
        {/* Slim progress bar — visible only while streaming on first load */}
        {isLoading && (
          <div className="h-1 w-full bg-black/5 dark:bg-white/10">
            <div
              className="h-1 bg-black dark:bg-white transition-all duration-300"
              style={{ width: `${loadingProgress || 5}%` }}
            />
          </div>
        )}
        {/* Table Header with Scroll Indicator */}
        <div className="bg-black/[0.03] dark:bg-white/5 px-4 sm:px-6 py-3 border-b border-black/8 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-4">
            <h3 className="text-sm font-semibold text-black dark:text-white">Program Details</h3>
            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
              <span>{visibleHeaders.length} columns visible</span>
              {visibleHeaders.length > 5 && (
                <>
                  <span className="mx-2">•</span>
                  <div className="flex items-center">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">Scroll right to see all columns</span>
                    <div className="flex ml-2 space-x-1">
                      <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-4 flex-wrap">
            {auth.currentUser && (
              <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Familiarity scores active
              </div>
            )}
            {Object.values(selectedFilters).some(f => f) && (
              <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {Object.values(selectedFilters).filter(f => f).length} filter(s) active
              </div>
            )}
            {lockedColumnsCount > 0 && (
              <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Step 2 scores locked
              </div>
            )}
            {visibleHeaders.length > 5 && (
              <div className="bg-black/5 dark:bg-white/10 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full text-xs font-medium">
                More columns →
              </div>
            )}
          </div>
        </div>
<div
  ref={tableContainerRef}
  className="overflow-x-auto overflow-y-auto relative w-full max-w-full"
  id="table-container"
  style={{ maxHeight: 'min(calc(100vh - 320px), 70vh)' }}
  onScroll={undefined}
>
          {/* Scroll indicator overlay */}
          {visibleHeaders.length > 5 && (
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-black/5 dark:from-white/10 to-transparent pointer-events-none z-10 flex items-center justify-center">
              <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          )}
          
          <table className="min-w-full divide-y divide-black/8 dark:divide-gray-700 border-collapse">
            <thead className="bg-black/[0.03] dark:bg-white/5">
              <tr>
                {/* When headers haven't streamed in yet, show animated placeholder cells */}
                {visibleHeaders.length === 0 && isLoading
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <th key={i} className="px-6 py-4 border-x-0 border-t-0 border-b-0">
                        <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" style={{ width: `${50 + (i * 13) % 40}%` }} />
                      </th>
                    ))
                  : visibleHeaders.map((header, index) => (
                  <th
                    key={index}
                    className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-black/[0.06] dark:hover:bg-white/10 transition-all duration-200 group relative border-x-0 border-t-0 border-b-0"
                    onClick={() => handleSort(header)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="flex items-center">
                        {header}
                        {header === 'Familiarity Score' && !hasCurrentSpecialtyAccess && (
                          <div className="ml-2 px-2 py-0.5 bg-black/10 dark:bg-white/10 text-gray-700 dark:text-gray-300 text-xs rounded-full font-medium">
                            Premium
                          </div>
                        )}
                        {header === 'Familiarity Score' && hasCurrentSpecialtyAccess && (
                          <div className="ml-2 px-2 py-0.5 bg-black/10 dark:bg-white/10 text-black dark:text-white text-xs rounded-full font-medium">
                            Active
                          </div>
                        )}
                        {isBlurred(header) && (
                          <div className="ml-2 px-2 py-0.5 bg-black/10 dark:bg-white/10 text-gray-700 dark:text-gray-300 text-xs rounded-full font-medium">
                            Premium
                          </div>
                        )}
                      </span>
                      <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-black dark:group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-black/6 dark:divide-gray-700">
              {/* Skeleton rows — visible while the very first batch is still downloading */}
              {isLoading && tableData.length === 0 && (
                Array.from({ length: 12 }).map((_, i) => (
                  <SkeletonRow key={i} colCount={visibleHeaders.length || 6} />
                ))
              )}
              {/* Render all loaded rows — Firestore pagination keeps this to a small batch */}
              {filteredData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  onClick={() => handleRowClick(row)}
                  className="hover:bg-black/[0.03] dark:hover:bg-white/5 transition-all duration-200 group cursor-pointer"
                >
                  {visibleHeaders.map((col, colIndex) => (
                    <td
                      key={colIndex}
                      className={`px-6 py-4 text-sm relative border-x-0 border-t-0 ${
                        (isBlurred(col) || (col === 'Familiarity Score' && isFamiliarityLocked()))
                          ? "text-gray-400 dark:text-gray-500 cursor-pointer relative group-hover:bg-black/[0.04] dark:group-hover:bg-white/5"
                          : isWebsiteColumn(col)
                          ? "text-gray-900 dark:text-gray-100 cursor-default"
                          : "text-gray-900 dark:text-gray-100"
                      }`}
                      onClick={
                        (isBlurred(col) || (col === 'Familiarity Score' && isFamiliarityLocked()))
                          ? handleLockedColumnClick
                          : isWebsiteColumn(col)
                          ? handleWebsiteColumnClick
                          : undefined
                      }
                    >
                      {renderCellContent(col, row, colIndex)}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Streaming progress banner — only while first-time download is live */}
              {isLoading && tableData.length > 0 && (
                <tr>
                  <td colSpan={visibleHeaders.length} className="px-6 py-2 bg-black/[0.03] dark:bg-white/5 text-center border-x-0 border-t-0">
                    <span className="text-xs text-gray-600 dark:text-gray-400 font-medium animate-pulse">
                      ⏳ Loading more rows… {Math.round(loadingProgress)}%
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Sentinel inside the scroll container so IntersectionObserver fires on scroll */}
          <div ref={sentinelRef} style={{ height: 1 }} />
        </div>

        {/* Load-more status shown below the scroll container */}
        <div className="py-2 flex justify-center">
          {loadingMore && (
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 py-3">
              <div className="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 border-t-gray-600 dark:border-t-gray-400 rounded-full animate-spin" />
              <span>Loading more programs…</span>
            </div>
          )}
          {allLoaded && tableData.length > 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500 py-2">All {tableData.length} programs loaded</span>
          )}
        </div>

        {filteredData.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No programs found</h3>
            <p className="text-gray-600 dark:text-gray-400">Try adjusting your search terms or filters</p>
          </div>
        )}
      </div>

      {/* Enhanced Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-4xl w-full border border-black/8 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-black/5 dark:bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-black dark:text-white mb-2 tracking-[-0.02em]">Advanced Filter</h3>
              <p className="text-[14px] text-gray-500 dark:text-gray-400">
                Filter programs based on specific criteria to find the best matches
              </p>
            </div>

            {/* Debug: Show filter count and details */}
            <div className="text-center mb-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing {getFilterConfig().length} filter options
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Filters: {getFilterConfig().map(f => f.label).join(', ')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {getFilterConfig().map((filter) => {
                const isFilterLocked = filter.isPremium && !hasCurrentSpecialtyAccess;
                const selectedValue = selectedFilters[filter.key];
                
                return (
                  <div
                    key={filter.key}
                    className={`p-6 rounded-2xl border-2 transition-all duration-200 ${
                      isFilterLocked 
                        ? "bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 cursor-not-allowed opacity-75"
                        : selectedValue
                        ? `bg-${filter.color}-50 dark:bg-gray-700 border-${filter.color}-300 dark:border-gray-600 shadow-lg`
                        : "bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{filter.icon}</span>
                        <div>
                          <h4 className="font-semibold text-lg text-gray-900 dark:text-white">{filter.label}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{filter.description}</p>
                        </div>
                      </div>
                      {isFilterLocked && (
                        <div className="w-6 h-6 bg-black/10 dark:bg-white/10 rounded-lg flex items-center justify-center">
                          <svg className="w-3 h-3 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Current Profile Value */}
                    {filter.userValue && (
                      <div className="mb-4 p-3 bg-black/[0.04] dark:bg-white/5 rounded-xl border border-black/8 dark:border-gray-600">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Your profile: <span className="font-semibold text-black dark:text-white">{filter.userValue}</span>
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Dropdown Selection */}
                    {isFilterLocked ? (
                      <div 
                        className="w-full p-3 bg-gray-200 dark:bg-gray-600 rounded-lg text-gray-500 dark:text-gray-400 text-center cursor-pointer"
                        onClick={() => {
                          setShowFilterModal(false);
                          setShowPaymentModal(true);
                        }}
                      >
                        🔒 Upgrade to filter by {filter.label}
                      </div>
                    ) : (
                      <select
                        value={selectedValue}
                        onChange={(e) => setSelectedFilters(prev => ({
                          ...prev,
                          [filter.key]: e.target.value
                        }))}
                        className="w-full p-3 border border-black/15 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 focus:border-black/30 dark:focus:border-gray-500 transition-colors"
                      >
                        <option value="">All {filter.label.toLowerCase()}</option>
                        {filter.options.map(option => (
                          <option key={option} value={option}>
                            {option}
                            {option === filter.userValue && " (Your profile)"}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Active Filters Summary */}
            {Object.values(selectedFilters).some(f => f) && (
              <div className="bg-black/[0.02] dark:bg-white/5 rounded-2xl p-4 mb-6 border border-black/8 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-black dark:text-white mb-2">Active Filters:</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(selectedFilters).map(([key, value]) => {
                    if (!value) return null;
                    const filter = getFilterConfig().find(f => f.key === key);
                    return (
                      <div key={key} className="inline-flex items-center bg-white dark:bg-gray-700 border border-black/10 dark:border-gray-600 rounded-full px-3 py-1 text-sm">
                        <span className="mr-1">{filter.icon}</span>
                        <span className="text-gray-700 dark:text-gray-200 mr-2">{filter.label}: {value}</span>
                        <button
                          onClick={() => setSelectedFilters(prev => ({ ...prev, [key]: "" }))}
                          className="text-gray-400 dark:text-gray-500 hover:text-black dark:hover:text-white"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setSelectedFilters({
                    country: "",
                    state: "",
                    graduation: "",
                    step1: "",
                    step2: "",
                    usce: "",
                    visa: ""
                  });
                }}
                className="flex-1 border border-black/15 dark:border-gray-600 hover:border-black/25 dark:hover:border-gray-500 text-black dark:text-white px-6 py-3 rounded-full font-medium transition-all"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="flex-1 bg-black/[0.06] dark:bg-gray-700 hover:bg-black/10 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-6 py-3 rounded-full font-medium transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="flex-1 bg-gradient-to-r from-gray-800 to-black dark:from-gray-600 dark:to-gray-800 hover:from-gray-700 hover:to-gray-900 dark:hover:from-gray-500 dark:hover:to-gray-700 text-white px-6 py-3 rounded-full font-medium transition-all duration-200 active:scale-[0.98] shadow-lg shadow-black/10 dark:shadow-black/20"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

{showPaymentModal && (
  <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 z-50">
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-black/8 dark:border-gray-700 w-full max-w-3xl overflow-hidden shadow-xl relative">
      {/* Close button */}
      <button
        onClick={() => setShowPaymentModal(false)}
        className="absolute top-4 right-4 z-10 p-2 rounded-full text-gray-400 dark:text-gray-500 hover:text-black dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
        aria-label="Close"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div className="flex flex-col sm:flex-row min-h-0 max-h-[90vh]">
        {/* Left: What you get */}
        <div className="sm:w-[44%] bg-black/[0.03] dark:bg-white/5 border-b sm:border-b-0 sm:border-r border-black/8 dark:border-gray-700 p-6 sm:p-8 flex flex-col justify-center">
          <h4 className="text-sm font-semibold text-black dark:text-white uppercase tracking-wider mb-4">What you get</h4>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-black/10 dark:bg-white/10 flex items-center justify-center mt-0.5">
                <svg className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <span className="text-sm text-gray-700 dark:text-gray-300 leading-snug">Step 2 CK cutoff requirements for {specialty}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-black/10 dark:bg-white/10 flex items-center justify-center mt-0.5">
                <svg className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <span className="text-sm text-gray-700 dark:text-gray-300 leading-snug">IMG composition & program data</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-black/10 dark:bg-white/10 flex items-center justify-center mt-0.5">
                <svg className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <span className="text-sm text-gray-700 dark:text-gray-300 leading-snug">Compatibility scores for your profile</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-black/10 dark:bg-white/10 flex items-center justify-center mt-0.5">
                <svg className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <span className="text-sm text-gray-700 dark:text-gray-300 leading-snug">Strategic application planning insights</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-black/10 dark:bg-white/10 flex items-center justify-center mt-0.5">
                <svg className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <span className="text-sm text-gray-700 dark:text-gray-300 leading-snug">Better match probability assessment</span>
            </li>
          </ul>
        </div>

        {/* Right: Price & actions */}
        <div className="sm:w-[56%] p-6 sm:p-8 flex flex-col overflow-y-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-gray-800 to-black dark:from-gray-600 dark:to-gray-700 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-black dark:text-white tracking-[-0.02em]">Unlock {specialty}</h3>
              <p className="text-[13px] text-gray-500 dark:text-gray-400">Lifetime access • One-time payment</p>
            </div>
          </div>

          {/* Currency */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment currency</span>
              <button
                onClick={() => setShowCurrencySelector(!showCurrencySelector)}
                className="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white flex items-center gap-1"
              >
                {showCurrencySelector ? "Hide" : "Change"}
                <svg className={`w-4 h-4 transition-transform ${showCurrencySelector ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            <div className="bg-black/[0.04] dark:bg-white/5 rounded-xl p-3 border border-black/8 dark:border-gray-600 flex items-center gap-3">
              <span className="text-xl">
                {getPopularCountriesForPayment().find(c => c.country === selectedPaymentCountry)?.flag || "🌍"}
              </span>
              <div>
                <div className="text-sm font-semibold text-black dark:text-white">{selectedPaymentCountry}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{getPricingInfo().currency}</div>
              </div>
            </div>
            {showCurrencySelector && (
              <div className="mt-2 border border-black/10 dark:border-gray-600 rounded-xl p-3 bg-black/[0.02] dark:bg-white/5 max-h-48 overflow-y-auto">
                {getPopularCountriesForPayment().map((countryData) => (
                  <button
                    key={countryData.country}
                    onClick={() => {
                      setSelectedPaymentCountry(countryData.country);
                      setShowCurrencySelector(false);
                    }}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-all ${
                      selectedPaymentCountry === countryData.country
                        ? "bg-black/10 dark:bg-white/10 border border-black/15 dark:border-gray-500 text-black dark:text-white"
                        : "hover:bg-black/[0.04] dark:hover:bg-white/5"
                    }`}
                  >
                    <span className="text-base">{countryData.flag}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{countryData.country}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{countryData.currency}</div>
                    </div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {formatCurrency(getPricingForCountrySync(countryData.country).amount, countryData.currency)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Price */}
          <div className="bg-black/[0.04] dark:bg-white/5 rounded-xl p-5 border border-black/8 dark:border-gray-600 mb-5">
            <div className="text-2xl sm:text-3xl font-bold text-black dark:text-white tracking-tight">
              {getPricingInfo().formattedAmount}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Equivalent to $10 USD • Live rates</p>
            {selectedPaymentCountry !== "India" && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Default: ₹{formatCurrency(getPricingForCountrySync("India").amount, "INR").replace("₹", "")} (India)
              </p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 mt-auto pt-2">
            <button
              onClick={() => setShowPaymentModal(false)}
              className="flex-1 border border-black/15 dark:border-gray-600 hover:border-black/25 dark:hover:border-gray-500 text-gray-700 dark:text-gray-200 px-4 py-3 rounded-full text-sm font-medium transition-all bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              disabled={paymentLoading}
            >
              Maybe Later
            </button>
            <button
              onClick={handlePayment}
              disabled={paymentLoading}
              className="flex-1 bg-gradient-to-r from-gray-800 to-black dark:from-gray-600 dark:to-gray-700 hover:from-gray-700 hover:to-gray-900 dark:hover:from-gray-500 dark:hover:to-gray-600 text-white px-4 py-3 rounded-full text-sm font-medium transition-all duration-200 active:scale-[0.98] shadow-lg shadow-black/10 dark:shadow-black/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {paymentLoading ? "Processing…" : `Pay ${getPricingInfo().formattedAmount}`}
            </button>
          </div>

          <p className="mt-4 text-[11px] text-gray-400 dark:text-gray-500">
            All currencies converted from $10 USD using live exchange rates.
          </p>
        </div>
      </div>
    </div>
  </div>
)}
    </div>
  );
}

export default TablePage;